import { useEffect, useRef, useState } from "react";
import NEWAPI from "../../API/newIndex";
import { adminSocket } from "../../utilities/socket";
import { useSelector } from "react-redux";
import ErrorPage from "../Main/ErrorPage";
import { t } from "i18next";
import { parentNavigate } from "../../controllers/ParentNavigate";

const STEP_LABELS = {
  product_categories: "lang_Product_Categories",
  products: "lang_Products",
  coupons: "lang_Coupons_Discounts",
  customers: "lang_Customers",
  orders: "lang_Orders_Last_60_Days",
  reviews: "lang_Reviews",
  pages: "lang_Pages",
  posts: "lang_Posts",
};

const ALL_STEPS = [
  "product_categories",
  "products",
  "coupons",
  "customers",
  "orders",
  "reviews",
  "pages",
  "posts",
];

const WooMigratingProgress = () => {
  const { clientData } = useSelector((state) => state.default);

  const searchParams = new URLSearchParams(window.location.search);
  const migrateFrom = searchParams.get("from");

  // ✅ Use refs so socket handlers always have fresh values
  const migrate_data = useRef(
    (localStorage.getItem("migrate_data") || "").split(",").filter(Boolean)
  );
  const wooPayload = useRef(
    JSON.parse(localStorage.getItem("woo_migrate_payload") || "{}")
  );

  // ✅ STEPS filtered from only what user selected
  const STEPS = useRef(
    ALL_STEPS.filter((step) => migrate_data.current.includes(step))
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [stepProgress, setStepProgress] = useState(0);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);

  const hasStarted = useRef(false);
  // ✅ Keep currentStep in a ref so socket handlers always read the latest value
  const currentStepRef = useRef(0);

  /* ---------------- SOCKET CONNECTION ---------------- */
  useEffect(() => {
    if (!adminSocket.connected) adminSocket.connect();

    const handleConnect = () => {
      adminSocket.emit("connectClient", {
        client_id: clientData._id.toString(),
      });
    };

    adminSocket.on("connect", handleConnect);

    // ✅ If already connected, emit immediately
    if (adminSocket.connected) {
      handleConnect();
    }

    return () => {
      adminSocket.off("connect", handleConnect);
    };
  }, []);

  /* ---------------- TRIGGER STEP ---------------- */
  const triggerStep = async (index) => {
    const steps = STEPS.current;

    // ✅ Guard: don't trigger if index is out of bounds
    if (index < 0 || index >= steps.length) {
      console.warn(`triggerStep called with invalid index: ${index}, total steps: ${steps.length}`);
      return;
    }

    try {
      await NEWAPI({
        url: "/admin/import/woocommerce",
        method: "post",
        data: {
          ...wooPayload.current,
          migrate: steps[index],
        },
      });
    } catch (err) {
      console.error("Migration step failed:", err);
      setError(err?.response?.data?.message || "Migration failed. Please try again.");
      setStatus("error");
    }
  };

  /* ---------------- HANDLE PROGRESS ---------------- */
  useEffect(() => {
    const handleProgress = (data) => {
      const steps = STEPS.current;
      const step = currentStepRef.current;

      // ✅ Use ref for current step — no stale closure
      if (!steps[step] || data.module !== steps[step]) return;

      setStepProgress(data.percent || 0);
    };

    adminSocket.on("import-progress", handleProgress);
    return () => adminSocket.off("import-progress", handleProgress);
  }, []); // ✅ Empty deps — handler uses refs, not state

  /* ---------------- HANDLE DONE ---------------- */
  useEffect(() => {
    const handleDone = (data) => {
      const steps = STEPS.current;
      const step = currentStepRef.current;

      // ✅ Use ref for current step — no stale closure
      if (!steps[step] || data.module !== steps[step]) return;

      setStepProgress(100);

      setTimeout(() => {
        const next = step + 1;

        if (next >= steps.length) {
          // ✅ All selected steps are done
          setStatus("success");
          localStorage.removeItem("woo_migrate_payload");
          localStorage.removeItem("migrate_data");
        } else {
          // ✅ Update both ref and state
          currentStepRef.current = next;
          setCurrentStep(next);
          setStepProgress(0);
          triggerStep(next);
        }
      }, 400);
    };

    adminSocket.on("import-done", handleDone);
    return () => adminSocket.off("import-done", handleDone);
  }, []); // ✅ Empty deps — handler uses refs, not state

  /* ---------------- START ---------------- */
  useEffect(() => {
    if (!wooPayload.current || !migrate_data.current.length) return;
    if (hasStarted.current) return;

    // ✅ Validate STEPS before starting
    if (STEPS.current.length === 0) {
      setError("No migration steps selected.");
      setStatus("error");
      return;
    }

    hasStarted.current = true;
    triggerStep(0);
  }, []);

  /* ---------------- GUARD: invalid state ---------------- */
  if (
    migrateFrom !== "woocommerce" ||
    !wooPayload.current ||
    !migrate_data.current.length
  ) {
    return <ErrorPage />;
  }

  /* ---------------- ERROR UI ---------------- */
  if (status === "error") {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="bg-white p-6 rounded-xl shadow text-center">
          <h2 className="text-red-600 text-lg font-semibold mb-2">
            {t("lang_Migration_failed")}
          </h2>
          <p className="text-gray-600 text-sm">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded"
          >
            {t("lang_Retry") || "Retry"}
          </button>
        </div>
      </div>
    );
  }

  /* ---------------- SUCCESS UI ---------------- */
  if (status === "success") {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="bg-white p-6 rounded-xl shadow text-center">
          <h2 className="text-green-600 text-lg font-semibold mb-2">
            {t("lang_Migration_complete")}
          </h2>
          <button
            onClick={() => parentNavigate("/dashboard")}
            className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded"
          >
            {t("lang_Go_to_Dashboard") || "Go to Dashboard"}
          </button>
        </div>
      </div>
    );
  }

  /* ---------------- PROGRESS UI ---------------- */
  const steps = STEPS.current;
  const overallProgress = ((currentStep + stepProgress / 100) / steps.length) * 100;
  const stepLabel = STEP_LABELS[steps[currentStep]] || "Processing";

  return (
    <div className="h-screen flex items-center justify-center">
      <div className="w-full max-w-md bg-white p-6 rounded-xl shadow">
        <h2 className="font-semibold text-lg mb-1">
          {t("lang_Migrating_your_store")}
        </h2>

        {/* Step indicators */}
        <div className="flex flex-wrap gap-2 mt-3 mb-4">
          {steps.map((step, idx) => (
            <span
              key={step}
              className={`text-xs px-2 py-1 rounded-full border ${
                idx < currentStep
                  ? "bg-green-100 border-green-400 text-green-700"
                  : idx === currentStep
                  ? "bg-indigo-100 border-indigo-400 text-indigo-700 font-semibold"
                  : "bg-gray-100 border-gray-300 text-gray-400"
              }`}
            >
              {idx < currentStep ? "✓ " : ""}
              {t(STEP_LABELS[step] || step)}
            </span>
          ))}
        </div>

        <p className="text-sm text-gray-600">
          {t("lang_Migrating")} <strong>{t(stepLabel)}</strong>...{" "}
          <span className="text-indigo-600">{stepProgress}%</span>
        </p>

        {/* Overall progress bar */}
        <div className="mt-3 h-2 bg-gray-200 rounded overflow-hidden">
          <div
            className="h-full bg-indigo-600 transition-all duration-300"
            style={{ width: `${overallProgress}%` }}
          />
        </div>

        <p className="text-xs mt-2 text-gray-500">
          {Math.round(overallProgress)}% {t("lang_completed") || "completed"} &nbsp;·&nbsp;
          {t("lang_Step") || "Step"} {currentStep + 1} {t("lang_of") || "of"} {steps.length}
        </p>
      </div>
    </div>
  );
};

export default WooMigratingProgress;